import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import UIkit from "uikit";
import { WorkSchedulesContext } from "../../providers/work_schedules.provider";
import {
  prettyTime,
  prettyRange,
  buildScheduleFromDateAvail,
  transformAvailabilityData,
} from "../../utils/availabilityUtils";

const pad2 = (n) => String(n).padStart(2, "0");

const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// Sunday start. If you prefer Monday start, adjust.
const startOfWeek = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay(); // 0..6
  x.setDate(x.getDate() - dow);
  return x;
};

const formatHeaderLabel = (iso) => {
  // "MM/DD/YY Mon"
  const d = new Date(`${iso}T00:00:00`);
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yy = String(d.getFullYear()).slice(-2);
  const dow = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  return `${mm}/${dd}/${yy} ${dow}`;
};

const isWeekend = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  const dow = d.getDay();
  return dow === 0 || dow === 6; // Sunday or Saturday
};

// Map a calendar row (one assignee-date combo) to the cell display shape
// used by the calendar grid.  Delegates the core status-branching logic
// to the shared buildScheduleFromDateAvail helper.
const getCellInfo = (row) => {
  if (!row) {
    return { className: "avail-cell avail-off", badge: "", sections: [], unscheduledText: "" };
  }

  // Build the canonical schedule object from the raw dateAvail fields
  const sched = buildScheduleFromDateAvail(row);

  const sections = [];
  const isOff = sched.status === "off";
  const isNoData = sched.status === "no_data";

  // Time off section
  if (sched.timeOff.length > 0) {
    const reason = sched.timeOff[0]?.desc || "Off";
    sections.push({
      title: "",
      items: [{ text: `Off all day: ${reason}` }],
    });
  }

  // Availability section
  if (sched.availability.length > 0) {
    sections.push({
      title: "Availability:",
      items: sched.availability.map((s) => ({ text: `${s.time} ${s.desc}` })),
    });
  }

  // Scheduled section (shown for ALL statuses, including off)
  if (sched.busy.length > 0) {
    sections.push({
      title: "Scheduled:",
      items: sched.busy.map((s) => ({ text: `${s.time} ${s.desc}` })),
    });
  }

  const unscheduledText = sched.unscheduledCount > 0
    ? `${sched.unscheduledCount} unscheduled task${sched.unscheduledCount > 1 ? "s" : ""} assigned`
    : "";

  const hasAvailability = sched.availability.length > 0;
  const badge = isNoData
    ? "No schedule data"
    : isOff
      ? (sched.busy.length > 0 ? "Off" : "No availability listed")
      : hasAvailability
        ? "Available"
        : "No availability listed";
  const className = hasAvailability ? "avail-cell avail-available" : "avail-cell avail-off";

  return { className, badge, sections, unscheduledText };
};

export default function AvailabilityCalendar({
  accountId = 5991692415008768,
  defaultMode = "week",
}) {
  const workSchedulesCtx = useContext(WorkSchedulesContext);
  const [mode, setMode] = useState(defaultMode);
  const [anchor, setAnchor] = useState(() => new Date());

  const [loading, setLoading] = useState(false);
  const [backendData, setBackendData] = useState([]); // Raw backend response
  const [selected, setSelected] = useState(null); // { assignee_id, assignee_type, work_date }

  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const cachedRangeRef = useRef({ start: null, end: null }); // tracks fetched date range

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Derive visible date columns
  const { start_date, end_date, columns, title } = useMemo(() => {
    const a = new Date(anchor);

    if (mode === "day") {
      const iso = toISO(a);
      return {
        start_date: iso,
        end_date: iso,
        columns: [{ iso, label: formatHeaderLabel(iso) }],
        title: iso,
      };
    }

    if (mode === "range") {
      const start = new Date(anchor);
      const cols = [];

      for (let i = 0; i < 15; i++) {
        const day = addDays(start, i);
        const iso = toISO(day);
        cols.push({ iso, label: formatHeaderLabel(iso) });
      }

      const end = addDays(start, 14);

      return {
        start_date: toISO(start),
        end_date: toISO(end),
        columns: cols,
        title: `${toISO(start)} – ${toISO(end)}`,
      };
    }

    // week
    const s0 = startOfWeek(a);
    const cols = [];
    for (let i = 0; i < 7; i++) {
      const day = addDays(s0, i);
      const iso = toISO(day);
      cols.push({ iso, label: formatHeaderLabel(iso) });
    }

    return {
      start_date: toISO(s0),
      end_date: toISO(addDays(s0, 6)),
      columns: cols,
      title: `Week of ${toISO(s0)}`,
    };
  }, [mode, anchor]);

  const load = useCallback(async (forceRefresh = false) => {
    if (inFlightRef.current) return;
    if (!workSchedulesCtx?.getAvailability) return;

    const cached = cachedRangeRef.current;

    if (!forceRefresh && cached.start && cached.end) {
      const reqS = new Date(`${start_date}T00:00:00`);
      const reqE = new Date(`${end_date}T00:00:00`);
      const cacS = new Date(`${cached.start}T00:00:00`);
      const cacE = new Date(`${cached.end}T00:00:00`);

      if (reqS >= cacS && reqE <= cacE) {
        // Fully covered by cache — nothing to fetch
        return;
      }
    }

    inFlightRef.current = true;
    setLoading(true);

    try {
      // Always fetch 15 days from the visible start_date
      const fetchStart = start_date;
      const fetchEndDate = new Date(`${start_date}T00:00:00`);
      fetchEndDate.setDate(fetchEndDate.getDate() + 14); // 15 days total
      const fetchEnd = toISO(fetchEndDate);

      const data = await workSchedulesCtx.getAvailability(fetchStart, fetchEnd);

      if (!mountedRef.current) return;

      const newData = Array.isArray(data) ? data : [];

      if (forceRefresh || !cached.start) {
        // First load or explicit refresh — replace everything
        setBackendData(newData);
        cachedRangeRef.current = { start: fetchStart, end: fetchEnd };
      } else {
        // Merge with existing data so previously-fetched ranges stay available
        setBackendData((prev) => {
          const merged = new Map();

          // Index existing assignees
          for (const a of prev) {
            merged.set(`${a.assignee_type}_${a.assignee_id}`, { ...a, availability: [...(a.availability || [])] });
          }

          // Merge new data
          for (const a of newData) {
            const key = `${a.assignee_type}_${a.assignee_id}`;
            const existing = merged.get(key);
            if (!existing) {
              merged.set(key, { ...a, availability: [...(a.availability || [])] });
            } else {
              // Merge availability dates — new data wins for overlapping dates
              const dateMap = new Map();
              for (const d of existing.availability) dateMap.set(d.date, d);
              for (const d of (a.availability || [])) dateMap.set(d.date, d);
              existing.availability = Array.from(dateMap.values()).sort((x, y) => x.date.localeCompare(y.date));
            }
          }

          return Array.from(merged.values());
        });

        // Expand cached range
        const prevStart = cached.start;
        const prevEnd = cached.end;
        cachedRangeRef.current = {
          start: fetchStart < prevStart ? fetchStart : prevStart,
          end: fetchEnd > prevEnd ? fetchEnd : prevEnd,
        };
      }
    } catch (e) {
      console.error(e);
      if (!mountedRef.current) return;
      UIkit.notification("Failed to load availability.", "danger");
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [workSchedulesCtx, start_date, end_date]);

  useEffect(() => {
    load();
  }, [load]);

  // React to scheduling changes from other components (e.g. AssigneePickerModal)
  // by selectively refetching the affected date and merging into cached data.
  useEffect(() => {
    const signal = workSchedulesCtx?.scheduleChangeSignal;
    if (!signal || !signal.date || signal.version === 0) return;
    if (!mountedRef.current) return;

    const changedDate = signal.date;

    // Only refetch if the changed date falls within (or near) our cached range
    const cached = cachedRangeRef.current;
    if (cached.start && cached.end && (changedDate < cached.start || changedDate > cached.end)) {
      return; // Outside our cached window — will be fetched naturally when user navigates there
    }

    (async () => {
      try {
        const data = await workSchedulesCtx.getAvailability(changedDate, changedDate);
        if (!mountedRef.current) return;
        const newData = Array.isArray(data) ? data : [];
        if (newData.length === 0) return;

        setBackendData((prev) => {
          const merged = new Map();
          for (const a of prev) {
            merged.set(`${a.assignee_type}_${a.assignee_id}`, { ...a, availability: [...(a.availability || [])] });
          }
          for (const a of newData) {
            const key = `${a.assignee_type}_${a.assignee_id}`;
            const existing = merged.get(key);
            if (!existing) {
              merged.set(key, { ...a, availability: [...(a.availability || [])] });
            } else {
              const dateMap = new Map();
              for (const d of existing.availability) dateMap.set(d.date, d);
              for (const d of (a.availability || [])) dateMap.set(d.date, d);
              existing.availability = Array.from(dateMap.values()).sort((x, y) => x.date.localeCompare(y.date));
            }
          }
          return Array.from(merged.values());
        });
      } catch (e) {
        console.error("Failed to refresh availability for changed date:", changedDate, e);
      }
    })();
  }, [workSchedulesCtx?.scheduleChangeSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Transform backend data to rows
  const rows = useMemo(() => {
    return transformAvailabilityData(backendData);
  }, [backendData]);

  // Filter rows to only show dates in current view
  const visibleRows = useMemo(() => {
    const visibleDates = new Set(columns.map(c => c.iso));
    return rows.filter(r => visibleDates.has(r.work_date));
  }, [rows, columns]);

  // People list (unique) - Staff first, then contacts
  const people = useMemo(() => {
    const m = new Map();
    for (const r of visibleRows) {
      const id = `${r.assignee_type}_${r.assignee_id}`;
      if (!m.has(id)) {
        m.set(id, {
          assignee_id: r.assignee_id,
          assignee_type: r.assignee_type,
          composite_id: id,
          name: r.name || "—",
          email: r.email || "",
        });
      }
    }
    return Array.from(m.values()).sort((a, b) => {
      // Staff (recipients) first, then contacts
      const aIsStaff = a.assignee_type === 'recipient';
      const bIsStaff = b.assignee_type === 'recipient';
      if (aIsStaff && !bIsStaff) return -1;
      if (!aIsStaff && bIsStaff) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [visibleRows]);

  // Index rows by assignee/date
  const rowIndex = useMemo(() => {
    const m = new Map();
    for (const r of visibleRows) {
      m.set(`${r.assignee_type}_${r.assignee_id}|${r.work_date}`, r);
    }
    return m;
  }, [visibleRows]);

  const selectedRow = useMemo(() => {
    if (!selected) return null;
    return rowIndex.get(`${selected.assignee_type}_${selected.assignee_id}|${selected.work_date}`) || null;
  }, [selected, rowIndex]);

  const goPrev = () => {
    setAnchor((d) => {
      if (mode === "day") return addDays(d, -1);
      if (mode === "week") return addDays(d, -7);
      if (mode === "range") return addDays(d, -15);
      return new Date(d.getFullYear(), d.getMonth() - 1, 1);
    });
  };

  const goNext = () => {
    setAnchor((d) => {
      if (mode === "day") return addDays(d, 1);
      if (mode === "week") return addDays(d, 7);
      if (mode === "range") return addDays(d, 15);      
      return new Date(d.getFullYear(), d.getMonth() + 1, 1);
    });
  };

  return (
    <section id="availabilty-calendar">
      <div id="hms-page-title">
        <h1 className="uk-heading-small">Availability Calendar</h1>
      </div>

      <div id="hms-main-body">
        <div
          className="uk-flex uk-flex-between uk-flex-middle uk-margin-small-bottom"
          style={{ gap: 12 }}
        >
          {/* Left: everything */}
          <div className="uk-flex uk-flex-middle uk-flex-wrap" style={{ gap: 10 }}>
            {/* View toggles */}
            <div className="uk-button-group">
              <button
                className={`checklist-item-type-btn uk-margin-small-right ${mode === "day" ? "active" : ""}`}
                type="button"
                onClick={() => setMode("day")}
              >
                Day
              </button>
              <button
                className={`checklist-item-type-btn uk-margin-small-right ${mode === "week" ? "active" : ""}`}
                type="button"
                onClick={() => setMode("week")}
              >
                Week
              </button>
              <button
                className={`checklist-item-type-btn uk-margin-small-right ${mode === "range" ? "active" : ""}`}
                type="button"
                onClick={() => setMode("range")}
              >
                15 Days
              </button>
            </div>

            {/* Prev / Title / Next / Refresh */}
            <div className="uk-flex uk-flex-middle" style={{ gap: 8 }}>
              <button
                className="uk-button uk-button-default uk-button-small"
                type="button"
                onClick={goPrev}
                uk-tooltip="Previous"
              >
                <span uk-icon="chevron-left"></span>
              </button>

              <div className="uk-text-bold" style={{ padding: "0 6px" }}>
                {title}
              </div>

              <button
                className="uk-button uk-button-default uk-button-small"
                type="button"
                onClick={goNext}
                uk-tooltip="Next"
              >
                <span uk-icon="chevron-right"></span>
              </button>

              <button
                className="uk-button uk-button-default uk-button-small"
                type="button"
                onClick={() => load(true)}
                disabled={loading}
                uk-tooltip="Refresh"
              >
                Refresh
              </button>
            </div>
          </div>

          {/* Right: empty (or put future filters here) */}
          <div />
        </div>
        <div className="avail-wrap">
          <div className="avail-table">
            {/* Header */}
            <div className="avail-row avail-header">
              <div className="avail-person sticky-col">Person</div>
              {columns.map((c) => {
                const colWidth = mode === "day" ? "300px" : "180px";
                return (
                <div 
                  key={c.iso} 
                  className="avail-col"
                  style={{
                    backgroundColor: isWeekend(c.iso) ? "#ffe6f0" : undefined,
                    flex: `0 0 ${colWidth}`,
                    minWidth: colWidth,
                    maxWidth: colWidth,
                    padding: "8px",
                    textAlign: "left",
                  }}
                >
                  {c.label}
                </div>
                );
              })}
            </div>

            {/* Body */}
            {loading ? (
              <div className="uk-padding uk-text-center">
                <span uk-spinner="ratio: 0.9" /> <span className="uk-text-meta">Loading…</span>
              </div>
            ) : people.length === 0 ? (
              <div className="uk-padding uk-text-center uk-text-muted">No data</div>
            ) : (
              people.map((p) => (
                <div key={p.composite_id} className="avail-row" style={{ display: "flex", alignItems: "stretch" }}>
                  <div className="avail-person sticky-col">
                    <div className="uk-text-bold" style={{ lineHeight: 1.1 }}>
                      {p.name}
                    </div>
                    {!!p.email && <div className="uk-text-meta">{p.email}</div>}
                  </div>

                  {columns.map((c) => {
                    const r = rowIndex.get(`${p.composite_id}|${c.iso}`);
                    const cellInfo = getCellInfo(r);
                    const isSel =
                      selected &&
                      selected.assignee_type === p.assignee_type &&
                      selected.assignee_id === p.assignee_id &&
                      selected.work_date === c.iso;

                    return (
                      <button
                        key={`${p.composite_id}-${c.iso}`}
                        type="button"
                        className={`avail-cell ${isSel ? "avail-selected" : ""}`}
                        style={{
                          backgroundColor: isWeekend(c.iso) ? "#ffe6f0" : "#ffffff",
                          flex: `0 0 ${mode === "day" ? "300px" : "180px"}`,
                          minWidth: mode === "day" ? "300px" : "180px",
                          maxWidth: mode === "day" ? "300px" : "180px",
                          padding: "8px",
                          textAlign: "left",
                          minHeight: "60px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                        }}
                        onClick={() => {
                          setSelected({ 
                            assignee_id: p.assignee_id, 
                            assignee_type: p.assignee_type,
                            work_date: c.iso 
                          });
                          UIkit.modal("#availability-details-modal").show();
                        }}
                      >
                        {cellInfo.badge && (
                          <span 
                            className={cellInfo.badge === "Available" ? "uk-label uk-label-success" : "uk-label uk-label-warning"}
                          >
                            {cellInfo.badge}
                          </span>
                        )}

                        {cellInfo.sections.length > 0 && (
                          <div style={{ marginTop: 4 }}>
                            {cellInfo.sections.map((section, idx) => (
                              <div key={idx} style={{ marginBottom: 4 }}>
                                {section.title && (
                                  <div style={{ fontSize: "11px", fontWeight: 600, marginBottom: 2, color: "#333" }}>
                                    {section.title}
                                  </div>
                                )}
                                {section.items.map((item, i) => (
                                  <div
                                    key={i}
                                    style={{ fontSize: "11px", marginBottom: 2, color: "#333", lineHeight: 1.4 }}
                                    title={item.text}
                                  >
                                    {item.text}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {!!cellInfo.unscheduledText && (
                          <div 
                            style={{ 
                              fontSize: "11px", 
                              marginTop: 6, 
                              color: "#999",
                              lineHeight: 1.4,
                            }} 
                            title={cellInfo.unscheduledText}
                          >
                            {cellInfo.unscheduledText}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
      <div id="availability-details-modal" uk-modal="true">
        <div className="uk-modal-dialog uk-modal-body">
          <button className="uk-modal-close-default" type="button" uk-close="true"></button>

          <h3 className="uk-modal-title">Availability Details</h3>

          {!selectedRow ? (
            <div className="uk-text-muted">No data for this day.</div>
          ) : (
            <div className="uk-text-small">
              <div className="uk-margin-small-bottom">
                <div className="uk-text-bold">{selectedRow.name}</div>
                <div className="uk-text-meta">{selectedRow.email}</div>
                <div className="uk-text-meta">{selectedRow.work_date}</div>
              </div>

              <hr />

              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1-3 uk-text-bold">Status</div>
                <div className="uk-width-2-3">{selectedRow.status}</div>

                {selectedRow.status === "off" && (
                  <>
                    <div className="uk-width-1-3 uk-text-bold">Reason</div>
                    <div className="uk-width-2-3">{selectedRow.reason || "Off"}</div>

                    {selectedRow.engagements?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Scheduled</div>
                        <div className="uk-width-2-3">
                          {selectedRow.engagements.map((eng, i) => (
                            <div key={i}>
                              {prettyRange(eng.start_time, eng.end_time)} • {eng.title || "Task"}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {selectedRow.unscheduled_work_orders?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Unscheduled</div>
                        <div className="uk-width-2-3">
                          {selectedRow.unscheduled_work_orders.length} task{selectedRow.unscheduled_work_orders.length > 1 ? "s" : ""}
                        </div>
                      </>
                    )}
                  </>
                )}

                {selectedRow.status === "working" && (
                  <>
                    {selectedRow.available_slots?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Available</div>
                        <div className="uk-width-2-3">
                          {selectedRow.available_slots.map((slot, i) => (
                            <div key={i}>
                              {prettyRange(slot.start_time, slot.end_time)}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {selectedRow.engagements?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Scheduled</div>
                        <div className="uk-width-2-3">
                          {selectedRow.engagements.map((eng, i) => (
                            <div key={i}>
                              {prettyRange(eng.start_time, eng.end_time)} • {eng.title || "Task"}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {selectedRow.unscheduled_work_orders?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Unscheduled</div>
                        <div className="uk-width-2-3">
                          {selectedRow.unscheduled_work_orders.length} task{selectedRow.unscheduled_work_orders.length > 1 ? "s" : ""}
                        </div>
                      </>
                    )}
                  </>
                )}

                {selectedRow.status === "no_schedule" && (
                  <>
                    {selectedRow.engagements?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Scheduled</div>
                        <div className="uk-width-2-3">
                          {selectedRow.engagements.map((eng, i) => (
                            <div key={i}>
                              {prettyRange(eng.start_time, eng.end_time)} • {eng.title || "Task"}
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {selectedRow.unscheduled_work_orders?.length > 0 && (
                      <>
                        <div className="uk-width-1-3 uk-text-bold">Unscheduled</div>
                        <div className="uk-width-2-3">
                          {selectedRow.unscheduled_work_orders.length} task{selectedRow.unscheduled_work_orders.length > 1 ? "s" : ""}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          <div className="uk-flex uk-flex-right uk-margin-top">
            <button className="uk-button uk-button-default uk-modal-close" type="button">
              Close
            </button>
          </div>
        </div>
      </div>      
   </section>
  );
}
